import React, { useEffect, useState } from 'react';
import { X, Users, Coins, TrendingUp, ArrowDownToLine, ArrowUpFromLine, RefreshCw, ShieldCheck, Wallet, Gift, BarChart3, Receipt, Search, Ban, Unlock as UnlockIcon, Pencil } from 'lucide-react';
import { adminApi } from '../api';
import { useToast } from '../hooks/use-toast';

const StatCard = ({ icon: Icon, label, value, sub, gradient }) => (
  <div className="rounded-2xl p-5 border border-slate-200 bg-white shadow-[0_8px_22px_rgba(15,23,42,0.05)] hover:shadow-[0_12px_28px_rgba(37,99,235,0.12)] transition-all">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white ${gradient}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="mt-3 text-[11px] font-bold tracking-[0.14em] text-slate-500">{label}</div>
    <div className="mt-1 text-2xl font-black text-slate-900">{value}</div>
    {sub && <div className="mt-0.5 text-[12px] text-slate-500">{sub}</div>}
  </div>
);

const Tab = ({ active, onClick, children }) => (
  <button onClick={onClick} className={`px-4 py-2 rounded-full text-[13px] font-extrabold transition ${active ? 'bg-blue-600 text-white shadow-[0_8px_18px_rgba(37,99,235,0.35)]' : 'bg-white text-slate-600 hover:bg-blue-50 border border-slate-200'}`}>
    {children}
  </button>
);

const AdminPage = ({ open, onClose }) => {
  const { toast } = useToast();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [txs, setTxs] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [airdropModal, setAirdropModal] = useState(null); // { user, detail }
  const [historyModal, setHistoryModal] = useState(null); // { user, data }
  const [adjustMode, setAdjustMode] = useState('set');
  const [adjustValue, setAdjustValue] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [recreditingId, setRecreditingId] = useState(null);
  const [fraudAlerts, setFraudAlerts] = useState([]);
  // Manual Recharge (admin → user)
  const [rechargeIdentifier, setRechargeIdentifier] = useState('');
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [rechargeReason, setRechargeReason] = useState('');
  const [rechargeLoading, setRechargeLoading] = useState(false);
  const [rechargeResult, setRechargeResult] = useState(null);

  const submitRecharge = async () => {
    if (!rechargeIdentifier.trim() || !rechargeAmount) {
      toast({ title: 'Champs manquants', description: 'Identifiant + montant requis', variant: 'destructive' });
      return;
    }
    setRechargeLoading(true);
    setRechargeResult(null);
    try {
      const res = await adminApi.creditUser({
        identifier: rechargeIdentifier.trim(),
        amount: Number(rechargeAmount),
        reason: rechargeReason.trim() || undefined,
      });
      setRechargeResult(res);
      toast({ title: '✅ Recharge effectuée', description: `+${res.amount_credited} DOMIA → ${res.email || res.username}` });
      setRechargeIdentifier('');
      setRechargeAmount('');
      setRechargeReason('');
    } catch (e) {
      toast({ title: 'Échec de la recharge', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setRechargeLoading(false);
    }
  };

  const loadFraudAlerts = async () => {
    try { setFraudAlerts(await adminApi.fraudAlerts()); } catch (_) { /* ignore */ }
  };

  const loadPendingDeposits = async () => {
    try {
      const p = await adminApi.pendingDeposits();
      setPendingDeposits(p);
    } catch (e) {
      toast({ title: 'Failed to load pending deposits', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const handleRecredit = async (paymentId) => {
    setRecreditingId(paymentId);
    try {
      const r = await adminApi.recreditDeposit(paymentId);
      if (r.credited) {
        toast({ title: 'Re-credited successfully', description: r.tokens ? `${r.tokens.toFixed(2)} DOMIA credited.` : 'Done.' });
      } else if (r.already_credited) {
        toast({ title: 'Already credited', description: `$${r.amount_usd}` });
      } else {
        toast({ title: 'Credit attempt failed', description: r.credit_error || 'See logs', variant: 'destructive' });
      }
      await Promise.all([loadPendingDeposits(), loadAll()]);
    } catch (e) {
      toast({ title: 'Re-credit failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setRecreditingId(null);
    }
  };

  const loadUsers = async (q = '') => {
    try {
      const u = await adminApi.users({ q, limit: 100 });
      setUsers(u);
    } catch (e) {
      toast({ title: 'Failed to load users', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, u, t, d] = await Promise.all([
        adminApi.stats(),
        adminApi.users({ limit: 50 }),
        adminApi.transactions(50),
        adminApi.deposits(50),
      ]);
      setStats(s); setUsers(u); setTxs(t); setDeposits(d);
    } catch (e) {
      toast({ title: 'Failed to load admin data', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) { loadAll(); loadPendingDeposits(); loadFraudAlerts(); } }, [open]);

  const handleBlock = async (u) => {
    const newState = !u.isBlocked;
    const reason = newState ? (prompt('Raison du blocage ?', 'Comportement suspect / triche') || '') : '';
    if (newState && !reason) return;
    try {
      await adminApi.blockUser(u.id, newState, reason);
      toast({ title: newState ? 'Compte suspendu' : 'Compte réactivé', description: u.email });
      await loadUsers(userQuery);
    } catch (e) {
      toast({ title: 'Action échouée', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const openAirdropModal = async (u) => {
    try {
      const detail = await adminApi.airdropDetail(u.id);
      setAirdropModal({ user: u, detail });
      setAdjustMode('set');
      setAdjustValue(String(detail.airdropBalance || 0));
      setAdjustReason('');
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const openGameHistoryModal = async (u) => {
    try {
      const data = await adminApi.userGameHistory(u.id, 200);
      setHistoryModal({ user: u, data });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const submitAdjust = async () => {
    const value = parseFloat(adjustValue);
    if (isNaN(value)) {
      toast({ title: 'Valeur invalide', variant: 'destructive' });
      return;
    }
    try {
      const res = await adminApi.airdropAdjust(airdropModal.user.id, { mode: adjustMode, value, reason: adjustReason });
      toast({ title: 'Solde ajusté', description: `${res.previous} → ${res.new_value} DOMIA (Δ ${res.delta >= 0 ? '+' : ''}${res.delta})` });
      setAirdropModal(null);
      await loadUsers(userQuery);
    } catch (e) {
      toast({ title: 'Action échouée', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  if (!open) return null;

  const fmt = (n, d = 2) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: d });

  return (
    <div className="fixed inset-0 z-[60] bg-slate-50 overflow-y-auto">
      <div className="max-w-[1300px] mx-auto px-5 md:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-slate-900 to-blue-800 flex items-center justify-center shadow-lg">
              <ShieldCheck className="w-6 h-6 text-amber-300" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">Admin Panel</h1>
              <p className="text-[13px] text-slate-500">Domino Arena · Real-time operations dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadAll} disabled={loading} className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 text-[13px] font-extrabold text-slate-700 hover:bg-blue-50 transition">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button onClick={onClose} className="bg-white border border-slate-200 rounded-full p-2.5 hover:bg-slate-100">
              <X className="w-4 h-4 text-slate-700" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-5 flex items-center gap-2 flex-wrap">
          <Tab active={tab === 'overview'} onClick={() => setTab('overview')}>Overview</Tab>
          <Tab active={tab === 'users'} onClick={() => setTab('users')}>Users ({users.length})</Tab>
          <Tab active={tab === 'pending'} onClick={() => setTab('pending')}>
            <span className="flex items-center gap-2">
              Pending <span className={`px-1.5 py-0.5 rounded text-[10px] font-black ${pendingDeposits.length > 0 ? 'bg-red-500 text-white animate-pulse' : 'bg-emerald-500 text-white'}`}>{pendingDeposits.length}</span>
            </span>
          </Tab>
          <Tab active={tab === 'fraud'} onClick={() => setTab('fraud')}>
            <span className="flex items-center gap-2">
              <span>🚨 Fraud</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-black ${fraudAlerts.length > 0 ? 'bg-red-600 text-white animate-pulse' : 'bg-emerald-500 text-white'}`}>{fraudAlerts.length}</span>
            </span>
          </Tab>
          <Tab active={tab === 'deposits'} onClick={() => setTab('deposits')}>Deposits ({deposits.length})</Tab>
          <Tab active={tab === 'transactions'} onClick={() => setTab('transactions')}>Transactions ({txs.length})</Tab>
          <Tab active={tab === 'recharge'} onClick={() => setTab('recharge')}>💰 Recharge</Tab>
        </div>

        {tab === 'overview' && stats && (
          <>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard icon={Users} label="TOTAL USERS" value={fmt(stats.users.total, 0)} sub={`${stats.users.verified} verified`} gradient="bg-gradient-to-br from-blue-700 to-blue-400" />
              <StatCard icon={Wallet} label="WALLETS LINKED" value={fmt(stats.users.wallet_linked, 0)} sub="Phantom wallets" gradient="bg-gradient-to-br from-emerald-600 to-emerald-400" />
              <StatCard icon={Gift} label="AIRDROP DONE" value={fmt(stats.users.airdrop_completed, 0)} sub="All 5 tasks completed" gradient="bg-gradient-to-br from-amber-500 to-amber-300" />
              <StatCard icon={Coins} label="TAX BALANCE" value={`${fmt(stats.admin_tax_balance)} DOMIA`} sub={`Total ${fmt(stats.admin_tax_collected)} collected`} gradient="bg-gradient-to-br from-slate-900 to-slate-700" />
            </div>

            <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Presale */}
              <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 shadow-[0_8px_22px_rgba(15,23,42,0.05)]">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-black text-slate-900">Presale State</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Metric label="Current Phase" value={`Phase ${stats.presale.currentPhase || 1}`} />
                  <Metric label="Tokens Sold" value={fmt(stats.presale.totalTokensSold)} />
                  <Metric label="USD Raised" value={`$${fmt(stats.presale.totalRaisedUsd)}`} />
                  <Metric label="Deposits" value={`${stats.deposits.count} ($${fmt(stats.deposits.total_usd)})`} />
                </div>
                <div className="mt-4 grid grid-cols-4 gap-2">
                  {[1, 2, 3, 4].map(p => {
                    const sold = stats.presale.tokensSoldByPhase?.[String(p)] || 0;
                    const cap = 100_000_000;
                    const pct = Math.min(100, (sold / cap) * 100);
                    return (
                      <div key={p} className="rounded-xl border border-slate-200 p-3 bg-slate-50">
                        <div className="text-[11px] font-bold text-slate-500">Phase {p}</div>
                        <div className="text-[13px] font-black text-slate-900 mt-0.5">{fmt(sold, 0)} / 100M</div>
                        <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-blue-600 to-blue-400" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Wallets */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-[0_8px_22px_rgba(15,23,42,0.05)]">
                <div className="flex items-center gap-2 mb-3">
                  <Wallet className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-black text-slate-900">Solana Wallets</h3>
                </div>
                <div className="space-y-3">
                  <WalletInfo label="Presale Payout" addr={stats.wallets.presale_wallet} />
                  <WalletInfo label="Game Payout" addr={stats.wallets.game_wallet} />
                  <WalletInfo label="DOMIA Mint" addr={stats.wallets.mint || 'Not configured'} small />
                  <div className="text-[11px] text-slate-500 pt-2 border-t border-slate-100">Network: <span className="font-bold text-slate-700">{stats.wallets.network}</span></div>
                </div>
              </div>
            </div>

            {/* Volumes */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-2xl p-5 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2"><ArrowDownToLine className="w-4 h-4 text-blue-600" /><span className="text-[11px] font-bold tracking-[0.14em] text-slate-600">DEPOSIT VOLUME</span></div>
                  <div className="text-3xl font-black text-slate-900 mt-1">${fmt(stats.deposits.total_usd)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] font-bold text-slate-500">COUNT</div>
                  <div className="text-2xl font-black text-blue-600">{stats.deposits.count}</div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-2xl p-5 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2"><ArrowUpFromLine className="w-4 h-4 text-amber-600" /><span className="text-[11px] font-bold tracking-[0.14em] text-slate-600">WITHDRAWAL VOLUME</span></div>
                  <div className="text-3xl font-black text-slate-900 mt-1">{fmt(stats.withdrawals.total_tokens)} DOMIA</div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] font-bold text-slate-500">COUNT</div>
                  <div className="text-2xl font-black text-amber-600">{stats.withdrawals.count}</div>
                </div>
              </div>
            </div>
          </>
        )}

        {tab === 'users' && (
          <div className="mt-5 space-y-3" data-testid="admin-users-tab">
            {/* Search bar */}
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-sm">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') loadUsers(userQuery); }}
                placeholder="Rechercher par email, username ou adresse wallet Phantom…"
                data-testid="admin-user-search"
                className="flex-1 outline-none text-[13px] text-slate-700 placeholder:text-slate-400 bg-transparent"
              />
              <button onClick={() => loadUsers(userQuery)} data-testid="admin-search-btn" className="px-3 py-1 rounded-lg bg-blue-600 text-white text-[12px] font-bold hover:bg-blue-700">Search</button>
              {userQuery && (
                <button onClick={() => { setUserQuery(''); loadUsers(''); }} className="text-slate-400 hover:text-slate-700 text-[12px] font-bold">Clear</button>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm overflow-x-auto">
              <table className="w-full text-[13px] min-w-[900px]">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-[11px] font-bold tracking-wide text-slate-500">
                    <Th>USER</Th><Th>EMAIL</Th><Th>WALLET PHANTOM</Th><Th>BALANCE</Th><Th>AIRDROP</Th><Th>STATUS</Th><Th>ACTIONS</Th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className={`border-b border-slate-100 ${u.isBlocked ? 'bg-red-50/40' : 'hover:bg-slate-50'}`} data-testid={`admin-user-row-${u.id}`}>
                      <Td><span className="font-bold text-slate-900">{u.username}</span></Td>
                      <Td className="text-slate-600">{u.email}</Td>
                      <Td className="font-mono text-[11px]" title={u.walletAddress || ''}>
                        {u.walletAddress ? `${u.walletAddress.slice(0, 6)}…${u.walletAddress.slice(-4)}` : <span className="text-slate-400">—</span>}
                      </Td>
                      <Td className="font-black text-slate-900">{fmt(u.walletBalance)}</Td>
                      <Td className="font-black text-amber-700">{fmt(u.airdropBalance)}</Td>
                      <Td>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${u.isBlocked ? 'bg-red-100 text-red-700' : u.emailVerified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {u.isBlocked ? 'BLOCKED' : u.emailVerified ? 'Verified' : 'Pending'}
                        </span>
                        {u.isAdmin && <span className="ml-1 inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700">Admin</span>}
                      </Td>
                      <Td>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => openAirdropModal(u)}
                            data-testid={`admin-adjust-btn-${u.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 text-[11px] font-bold transition"
                            title="Voir tâches Airdrop & ajuster solde"
                          >
                            <Pencil className="w-3 h-3" /> Airdrop
                          </button>
                          <button
                            onClick={() => openGameHistoryModal(u)}
                            data-testid={`admin-history-btn-${u.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-800 text-[11px] font-bold transition"
                            title="Voir l'historique des jeux (Domino, Ludo, TicTacToe)"
                          >
                            <Pencil className="w-3 h-3" /> Historique
                          </button>
                          {!u.isAdmin && (
                            <button
                              onClick={() => handleBlock(u)}
                              data-testid={`admin-block-btn-${u.id}`}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold transition ${u.isBlocked ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-800' : 'bg-red-100 hover:bg-red-200 text-red-800'}`}
                            >
                              {u.isBlocked ? (<><UnlockIcon className="w-3 h-3" /> Débloquer</>) : (<><Ban className="w-3 h-3" /> Bloquer</>)}
                            </button>
                          )}
                        </div>
                      </Td>
                    </tr>
                  ))}
                  {users.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-8">No users found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'pending' && (
          <div className="mt-5 space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
              <span className="text-amber-700 font-black text-xl">⚠️</span>
              <div className="flex-1">
                <div className="text-[13px] font-extrabold text-amber-900 tracking-wide">PAID-BUT-NOT-CREDITED</div>
                <div className="text-[12px] text-amber-800 mt-0.5">These NOWPayments deposits are confirmed but the auto-credit logic missed them. Click <b>Re-credit</b> to apply DOMIA tokens to the user manually.</div>
              </div>
              <button
                onClick={loadPendingDeposits}
                className="rounded-lg bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 text-[11px] font-extrabold tracking-wide"
                data-testid="pending-refresh-btn"
              >REFRESH</button>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid="pending-deposits-panel">
              <table className="w-full text-[13px]">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-[11px] font-bold tracking-wide text-slate-500">
                    <Th>PAYMENT ID</Th><Th>USER</Th><Th>AMOUNT</Th><Th>CURRENCY</Th><Th>STATUS</Th><Th>ERROR</Th><Th>DATE</Th><Th>ACTION</Th>
                  </tr>
                </thead>
                <tbody>
                  {pendingDeposits.map(d => (
                    <tr key={d.payment_id} className="border-b border-slate-100 hover:bg-amber-50/40" data-testid={`pending-row-${d.payment_id}`}>
                      <Td className="font-mono text-[11px]">{d.payment_id}</Td>
                      <Td>
                        <div className="font-semibold text-slate-900">{d.user_username || '—'}</div>
                        <div className="text-[10px] text-slate-500">{d.user_email}</div>
                      </Td>
                      <Td className="font-black">${fmt(d.amount_usd)}</Td>
                      <Td className="uppercase">{d.pay_currency}</Td>
                      <Td>
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">
                          {(d.status || 'unknown').toUpperCase()}
                        </span>
                      </Td>
                      <Td className="text-[10px] text-rose-600 max-w-[180px] truncate" title={d.credit_error || ''}>{d.credit_error || '—'}</Td>
                      <Td className="text-slate-500 text-[11px]">{d.createdAt?.slice(0, 16).replace('T', ' ')}</Td>
                      <Td>
                        <button
                          onClick={() => handleRecredit(d.payment_id)}
                          disabled={recreditingId === d.payment_id}
                          className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-[11px] font-extrabold disabled:opacity-50"
                          data-testid={`pending-recredit-${d.payment_id}`}
                        >
                          {recreditingId === d.payment_id ? '...' : 'RE-CREDIT'}
                        </button>
                      </Td>
                    </tr>
                  ))}
                  {pendingDeposits.length === 0 && (
                    <tr><td colSpan={8} className="text-center text-emerald-600 font-bold py-10" data-testid="pending-empty">✅ No pending deposits — all good!</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'deposits' && (
          <div className="mt-5 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[11px] font-bold tracking-wide text-slate-500">
                  <Th>ORDER ID</Th><Th>USER</Th><Th>AMOUNT USD</Th><Th>CURRENCY</Th><Th>PURPOSE</Th><Th>STATUS</Th><Th>DATE</Th>
                </tr>
              </thead>
              <tbody>
                {deposits.map(d => (
                  <tr key={d._id} className="border-b border-slate-100 hover:bg-slate-50">
                    <Td className="font-mono text-[11px]">{d.order_id?.slice(0, 12)}</Td>
                    <Td className="font-mono text-[11px]">{(d.user_id || '').slice(0, 8)}</Td>
                    <Td className="font-black">${fmt(d.amount_usd)}</Td>
                    <Td className="uppercase">{d.pay_currency}</Td>
                    <Td>{d.purpose}</Td>
                    <Td>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${d.credited ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                        {d.credited ? 'CREDITED' : (d.status || 'waiting').toUpperCase()}
                      </span>
                    </Td>
                    <Td className="text-slate-500 text-[11px]">{d.createdAt?.slice(0, 16).replace('T', ' ')}</Td>
                  </tr>
                ))}
                {deposits.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-8">No deposits yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'fraud' && (
          <div className="mt-5 space-y-3">
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-start gap-3">
              <span className="text-rose-700 font-black text-xl">🚨</span>
              <div className="flex-1">
                <div className="text-[13px] font-extrabold text-rose-900 tracking-wide">ANTI-FRAUD MONITOR</div>
                <div className="text-[12px] text-rose-800 mt-0.5">Auto-detected signals: large deposits stuck &gt;10 min, suspicious winnings (&gt;5000 DOMIA / 1h), blocked users still active.</div>
              </div>
              <button onClick={loadFraudAlerts} className="rounded-lg bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 text-[11px] font-extrabold tracking-wide" data-testid="fraud-refresh-btn">SCAN</button>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid="fraud-alerts-panel">
              {fraudAlerts.length === 0 ? (
                <div className="text-center text-emerald-600 font-bold py-12" data-testid="fraud-empty">✅ Aucun signal de fraude — système sain</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {fraudAlerts.map((a, i) => (
                    <li key={i} className="px-4 py-3 flex items-start gap-3 hover:bg-rose-50/30" data-testid={`fraud-alert-${i}`}>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-black ${a.severity === 'HIGH' ? 'bg-rose-600 text-white' : a.severity === 'MEDIUM' ? 'bg-amber-500 text-white' : 'bg-slate-400 text-white'}`}>{a.severity}</span>
                      <div className="flex-1">
                        <div className="text-[13px] font-bold text-slate-900">{a.message}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {a.user_email && <>👤 {a.user_username || '—'} · {a.user_email}</>}
                          {a.payment_id && <> · 💳 {a.payment_id}</>}
                          {a.is_blocked && <> · <span className="text-rose-600 font-bold">BLOCKED</span></>}
                        </div>
                      </div>
                      <span className="text-[10px] font-mono text-slate-400">{a.kind}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {tab === 'recharge' && (
          <div className="mt-5 space-y-4">
            <div className="bg-gradient-to-br from-emerald-50 to-blue-50 border border-emerald-200 rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <span className="text-3xl">💰</span>
                <div className="flex-1">
                  <h3 className="text-[15px] font-extrabold text-slate-900">Recharge manuelle (hors blockchain)</h3>
                  <p className="text-[12px] text-slate-600 mt-0.5">
                    Crédite directement le solde d'un utilisateur. Cible : email, username, wallet Solana, ou User ID. Pour <strong>débiter</strong>, entre un montant négatif (ex: <code>-500</code>).
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-3" data-testid="admin-recharge-form">
              <label className="block">
                <span className="text-[11px] font-bold tracking-wide text-slate-500">IDENTIFIANT (email / username / wallet / user ID)</span>
                <input
                  type="text"
                  value={rechargeIdentifier}
                  onChange={(e) => setRechargeIdentifier(e.target.value)}
                  placeholder="email@example.com ou Username ou 9ktBHs2..."
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-[14px] font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="recharge-identifier-input"
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-bold tracking-wide text-slate-500">MONTANT (DOMIA) — peut être négatif</span>
                <input
                  type="number"
                  step="0.01"
                  value={rechargeAmount}
                  onChange={(e) => setRechargeAmount(e.target.value)}
                  placeholder="1500"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-[16px] font-extrabold focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="recharge-amount-input"
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-bold tracking-wide text-slate-500">RAISON (optionnel)</span>
                <input
                  type="text"
                  value={rechargeReason}
                  onChange={(e) => setRechargeReason(e.target.value)}
                  placeholder="Promo / Compensation / Test ..."
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="recharge-reason-input"
                />
              </label>
              <button
                onClick={submitRecharge}
                disabled={rechargeLoading || !rechargeIdentifier.trim() || !rechargeAmount}
                className="w-full mt-2 rounded-xl bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-extrabold tracking-wide py-3 text-[14px] shadow-[0_6px_18px_rgba(16,185,129,0.25)] transition"
                data-testid="recharge-submit-btn"
              >
                {rechargeLoading ? 'En cours…' : 'VALIDER LA RECHARGE'}
              </button>
            </div>

            {rechargeResult && (
              <div className="bg-emerald-50 border border-emerald-300 rounded-2xl p-4 shadow-sm" data-testid="recharge-result">
                <div className="text-[12px] font-bold text-emerald-800 tracking-wide">✅ DERNIÈRE RECHARGE</div>
                <div className="mt-1 text-[13px] text-slate-800">
                  <strong>{rechargeResult.email || rechargeResult.username}</strong> <span className="text-slate-500 font-mono text-[11px]">({rechargeResult.user_id})</span>
                </div>
                <div className="mt-1 grid grid-cols-2 gap-2 text-[12px]">
                  <div><span className="text-slate-500">Crédit :</span> <strong className={rechargeResult.amount_credited >= 0 ? 'text-emerald-700' : 'text-rose-700'}>{rechargeResult.amount_credited >= 0 ? '+' : ''}{rechargeResult.amount_credited} DOMIA</strong></div>
                  <div><span className="text-slate-500">Nouveau solde :</span> <strong>{rechargeResult.new_walletBalance.toFixed(2)} DOMIA</strong></div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'transactions' && (
          <div className="mt-5 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[11px] font-bold tracking-wide text-slate-500">
                  <Th>DATE / TIME</Th><Th>TYPE</Th><Th>USER (GMAIL)</Th><Th>OPPONENT</Th><Th>AMOUNT</Th><Th>STATUS</Th><Th>DETAILS</Th>
                </tr>
              </thead>
              <tbody>
                {txs.map(t => {
                  const isWin = (t.type || '').includes('win') || t.outcome === 'win';
                  const isLose = (t.type || '').includes('loss') || t.outcome === 'loss';
                  const isRefund = (t.type || '').includes('refund');
                  const status = isRefund ? 'REFUND' : isWin ? '✅ WIN' : isLose ? '❌ LOSS' : (t.type || '').includes('deposit') ? '💰 DEPOSIT' : '·';
                  const statusCls = isWin ? 'bg-emerald-100 text-emerald-700' : isLose ? 'bg-rose-100 text-rose-700' : isRefund ? 'bg-amber-100 text-amber-700' : (t.type || '').includes('deposit') ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600';
                  return (
                    <tr key={t._id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`tx-row-${t._id}`}>
                      <Td className="text-[11px]">
                        <div className="font-bold text-slate-900">{t.date || '—'}</div>
                        <div className="text-slate-500">{t.time || ''}</div>
                      </Td>
                      <Td><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${typeColor(t.type)}`}>{t.type}</span></Td>
                      <Td>
                        <div className="font-semibold text-slate-900 text-[12px]">{t.user_username || (t.user_id || '').slice(0, 8)}</div>
                        <div className="text-[10px] text-slate-500">{t.user_email || '—'}</div>
                      </Td>
                      <Td>
                        {t.opponent_username ? (
                          <>
                            <div className="font-semibold text-slate-900 text-[12px]">{t.opponent_username}</div>
                            <div className="text-[10px] text-slate-500">{t.opponent_email || '—'}</div>
                          </>
                        ) : <span className="text-slate-400 text-[11px]">—</span>}
                      </Td>
                      <Td className="font-black">{fmt(t.total_tokens || t.amount_tokens || t.amount_usd || t.amount || 0)}</Td>
                      <Td><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${statusCls}`}>{status}</span></Td>
                      <Td className="text-slate-500 text-[11px]">
                        {t.bonus_percent !== undefined && `Bonus ${t.bonus_percent}%`}
                        {t.tax !== undefined && ` Tax ${t.tax}`}
                        {t.payment_id && <span className="font-mono"> · {t.payment_id}</span>}
                        {t.game && <span> · 🎮 {t.game}</span>}
                      </Td>
                    </tr>
                  );
                })}
                {txs.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-8">No transactions yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        <div className="h-10" />
      </div>

      {/* === Airdrop Moderation Modal === */}
      {airdropModal && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center px-4" onClick={() => setAirdropModal(null)} data-testid="admin-airdrop-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-lg w-full rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-5 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5" />
                <div>
                  <div className="text-[11px] font-bold opacity-80 uppercase">Modération Airdrop</div>
                  <div className="text-lg font-black">{airdropModal.user.username} <span className="opacity-70 text-[12px]">— {airdropModal.user.email}</span></div>
                </div>
              </div>
              <button onClick={() => setAirdropModal(null)} className="p-1 hover:bg-white/20 rounded-full"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-2">
                <Metric label="AIRDROP" value={`${fmt(airdropModal.detail.airdropBalance)} DOMIA`} />
                <Metric label="WALLET" value={`${fmt(airdropModal.detail.walletBalance)} DOMIA`} />
              </div>

              <div>
                <div className="text-[11px] font-black tracking-widest text-slate-500 mb-2">TÂCHES VALIDÉES ({airdropModal.detail.tasksCompleted})</div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.keys(airdropModal.detail.airdropTasks || {}).length === 0 && (
                    <span className="text-slate-400 text-[12px]">Aucune tâche validée pour le moment.</span>
                  )}
                  {Object.entries(airdropModal.detail.airdropTasks || {}).map(([task, done]) => (
                    <span key={task} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${done ? 'bg-emerald-100 text-emerald-700 border border-emerald-300' : 'bg-slate-100 text-slate-500'}`}>
                      {done ? '✓' : '○'} {task}
                    </span>
                  ))}
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <div className="text-[11px] font-black tracking-widest text-slate-500 mb-2">AJUSTER LE SOLDE AIRDROP</div>
                <div className="flex items-center gap-2 mb-2">
                  <button onClick={() => setAdjustMode('set')} className={`px-3 py-1.5 rounded-lg text-[12px] font-bold border ${adjustMode === 'set' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200'}`}>Définir</button>
                  <button onClick={() => setAdjustMode('delta')} className={`px-3 py-1.5 rounded-lg text-[12px] font-bold border ${adjustMode === 'delta' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200'}`}>Ajouter (+/-)</button>
                  <button onClick={() => { setAdjustMode('set'); setAdjustValue('0'); setAdjustReason('Reset (abus suspecté)'); }} className="ml-auto text-[12px] font-bold text-red-600 hover:text-red-800">Reset à 0</button>
                </div>
                <input
                  type="number"
                  value={adjustValue}
                  onChange={(e) => setAdjustValue(e.target.value)}
                  data-testid="admin-airdrop-adjust-input"
                  placeholder={adjustMode === 'set' ? 'Nouveau solde DOMIA' : '+ ou - DOMIA'}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-[14px] font-bold focus:outline-none focus:border-blue-500"
                />
                <input
                  type="text"
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  placeholder="Raison (audit log)"
                  className="w-full mt-2 border border-slate-200 rounded-lg px-3 py-2 text-[12px] focus:outline-none focus:border-blue-500"
                />
                <button
                  onClick={submitAdjust}
                  data-testid="admin-airdrop-adjust-submit"
                  className="mt-3 w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white font-black py-2.5 rounded-xl uppercase text-[12px] tracking-widest hover:opacity-90"
                >
                  Appliquer l&apos;ajustement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {historyModal && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center px-4" onClick={() => setHistoryModal(null)} data-testid="admin-history-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-5 py-3 flex items-center justify-between shrink-0">
              <div>
                <div className="text-[11px] font-bold tracking-[0.14em] text-blue-100">GAME HISTORY · vs AI</div>
                <div className="text-[15px] font-black">{historyModal.user.username} · {historyModal.user.email}</div>
              </div>
              <button onClick={() => setHistoryModal(null)} className="p-1 hover:bg-white/20 rounded-full"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              {historyModal.data?.hasChallengeBadge && (
                <div className="mb-3 rounded-xl border border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 px-3 py-2 text-[12px] font-black text-amber-900">
                  🏆 CHAMPION — Won the Human vs AI challenge (3 consecutive wins)
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <Metric label="WINS" value={historyModal.data?.stats?.wins || 0} />
                <Metric label="LOSSES" value={historyModal.data?.stats?.losses || 0} />
                <Metric label="TIES" value={historyModal.data?.stats?.ties || 0} />
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <Metric label="TOTAL BET" value={`${(historyModal.data?.stats?.total_bet || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} DOMIA`} />
                <Metric label="TOTAL WON" value={`+${(historyModal.data?.stats?.total_won || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} DOMIA`} />
                <Metric label="STREAK vs AI" value={historyModal.data?.consecutiveAiWins ?? 0} />
              </div>
              <div className="text-[11px] font-bold tracking-[0.14em] text-slate-500 mb-2">MATCH LOG · {historyModal.data?.items?.length || 0}</div>
              <div className="space-y-1.5">
                {(historyModal.data?.items || []).length === 0 && (
                  <div className="text-center py-6 text-slate-400 text-[13px]">No matches recorded</div>
                )}
                {(historyModal.data?.items || []).map((m) => {
                  const resultColor = m.result === 'win' ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                                    : m.result === 'loss' ? 'text-rose-700 bg-rose-50 border-rose-200'
                                    : 'text-slate-700 bg-slate-50 border-slate-200';
                  return (
                    <div key={m.match_id} className={`rounded-lg border px-3 py-2 flex items-center justify-between ${resultColor}`} data-testid={`admin-match-row-${m.match_id}`}>
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="text-[11px] font-black tracking-widest uppercase shrink-0">{m.game}</span>
                        <span className="text-[11px] font-bold uppercase">{m.result}</span>
                        <span className="text-[10px] opacity-70 truncate">{m.settledAt && new Date(m.settledAt).toLocaleString()}</span>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[13px] font-black tabular-nums">
                          {m.result === 'win' ? `+${m.credit.toLocaleString(undefined, { maximumFractionDigits: 0 })}` :
                           m.result === 'loss' ? `-${m.bet.toLocaleString(undefined, { maximumFractionDigits: 0 })}` :
                           `${m.credit.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                        </div>
                        <div className="text-[9px] opacity-70">DOMIA · bet {m.bet.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Metric = ({ label, value }) => (
  <div className="bg-slate-50 rounded-xl px-3 py-2.5">
    <div className="text-[10px] font-bold tracking-[0.14em] text-slate-500">{label}</div>
    <div className="text-[14px] font-black text-slate-900 mt-0.5">{value}</div>
  </div>
);

const WalletInfo = ({ label, addr, small }) => (
  <div className={`rounded-xl border border-slate-200 px-3 py-2 ${small ? 'bg-slate-50' : 'bg-blue-50/40'}`}>
    <div className="text-[10px] font-bold tracking-[0.14em] text-slate-500">{label}</div>
    <div className="text-[11px] font-mono text-slate-800 break-all mt-0.5">{addr || '—'}</div>
  </div>
);

const Th = ({ children }) => <th className="px-4 py-3">{children}</th>;
const Td = ({ children, className = '' }) => <td className={`px-4 py-2.5 ${className}`}>{children}</td>;

const typeColor = (t) => {
  if (t?.includes('presale')) return 'bg-blue-100 text-blue-700';
  if (t?.includes('airdrop')) return 'bg-amber-100 text-amber-700';
  if (t?.includes('withdraw')) return 'bg-red-100 text-red-700';
  if (t?.includes('pvp')) return 'bg-purple-100 text-purple-700';
  return 'bg-slate-100 text-slate-700';
};

export default AdminPage;
